/* eslint-disable react-hooks/exhaustive-deps */
import TextField from 'apollo-react/components/TextField/TextField';
import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';

import { useSsNumericTextBoxParams } from '../../custom-hooks';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { getUITA } from '../../utils/commonUtils';
import Browser from '../../utils/pdf-print-js/browser';
import { isNilOrEmpty } from '../../utils/ramdaUtils';
import { getDisplayValue, safeToFixedDecimalScale } from '../utils/controls/ssNumericTextBox.utils';
import { showAsteriskBefore } from '../utils/form-manager.utils';

const SSNumericTextBox = ({
  label,
  actions,
  className,
  isRequired,
  value,
  error,
  entityName,
  fieldName,
  inTable,
  inFieldGroup,
  isHiddenLabel,
  instanceId,
  isEditor,
  disabled,
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);
  const [hasValueChanged, setHasValueChanged] = useState(false);
  const ref = useRef(null);
  const translateString = useTranslation();

  const { dataType, decimalScale } = useSsNumericTextBoxParams({
    entityName,
    fieldName,
    value: localValue,
  });

  const displayValue = getDisplayValue({ isFocused, hasValueChanged, decimalScale, localValue });
  const hiddenLabel = inTable || (inFieldGroup && isHiddenLabel);

  // Update inner state when `value` is changed from outside.
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleBlur = ({ target: { value: blurValue } }) => {
    if ((isNilOrEmpty(value) && blurValue === '') || blurValue === value?.toString()) {
      return;
    }

    if (blurValue === '') {
      actions.updateControlValue(null);
      return;
    }

    let newLocalValue = blurValue;
    const valueToStore =
      blurValue.includes('e') && !Browser.isFirefox() ? +blurValue.split('e')[0] : +blurValue;

    actions.updateControlValue(valueToStore);

    switch (dataType) {
      case 'System.Int32': {
        newLocalValue = parseInt(blurValue);
        break;
      }
      case 'System.Decimal': {
        newLocalValue = +safeToFixedDecimalScale(decimalScale, blurValue);
        break;
      }
      default:
        // eslint-disable-next-line no-console
        console.log(`Unhandled dataType: ${dataType}`);
    }

    setLocalValue(newLocalValue);
    setHasValueChanged(true);
    setIsFocused(false);
  };

  const handleChange = ({ target }) => {
    // we need to manually set focus to the field, when value is changed by stepper arrows
    // some browsers like Firefox does not set focus automatically
    if (process.env.NODE_ENV === 'test') {
      setIsFocused(true); // forcing focused state on test environment when we interact with the field
    } else {
      ref.current.focus(); // by some reason it causes tests to fail
    }

    setHasValueChanged(false);
    setLocalValue(target.value);
  };

  return (
    <TextField
      id={instanceId}
      type={Browser.isFirefox() ? 'text' : 'number'}
      style={{ width: '100%', maxWidth: 568, padding: isEditor ? 4 : 0 }}
      label={hiddenLabel ? '' : label}
      asteriskPosition={showAsteriskBefore(inTable ? '' : label, inTable) ? 'before' : 'after'}
      className={className}
      onFocus={() => setIsFocused(true)}
      onBlur={handleBlur}
      onChange={handleChange}
      required={isRequired}
      value={displayValue ?? ''}
      error={error}
      disabled={isEditor || disabled}
      fullWidth={inTable}
      inputProps={{
        ref,
        title:
          isRequired && (_.isNil(value) || value === '')
            ? translateString('Please fill out this field.')
            : '',
      }}
      onWheel={() => {
        const input = ref.current;
        if (input && document.activeElement === input) {
          // this will prevent accidental scroll to change inputs value
          input.blur();
        }
      }}
      ui-ta={getUITA('FormCtrl', 'SSNumericTextBox', entityName, fieldName)}
    />
  );
};

export default SSNumericTextBox;
